import React, { useEffect, useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { Trash2 } from "lucide-react";
import { useGenerateCampaignFormGoogle } from "../../../../hooks/platforms/google/useGenerateCampaignFormGoogle";
import AnswerContainer from "../../../TypeForm/AnswerContainer";
import AnswerContainerTitleAndField from "../../../TypeForm/AnswerContainerTitleAndField";
import {
  Alert,
  LinearProgress,
  Skeleton,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField as TextFieldMUI,
} from "@mui/material";
import AnswerTitle from "../../../TypeForm/AnswerTitle";
import AnswerSubTitle from "../../../TypeForm/AnswerSubtitle";
import ActionsButtons from "../../../TypeForm/ActionsButtons";
import { useTranslationApp } from "../../../../lib/i18next";
import Select from "../../../Form/Select";
import { optionsCampaignsTypeGoogleAds } from "../../../../utils/options";
import {
  AD_ACCOUNT_GOOGLE_PLACEHOLDER,
  CREATE_ADS_CAMPAIGN_BUDGET_PLACEHOLDER,
  CREATE_CAMPAIGN_TRAFFIC_LINK_PLACEHOLDER,
  SELECT_A_OPTION,
  VALUE_EXPRESSED_IN,
  START_DATE_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  CONVERSION_ACTIONS_PLACEHOLDER,
  ERROR_OCCURRED,
  MERCHANT_CENTER_ID_PLACEHOLDER,
  APP_PLACEHOLDER,
  DEEP_LINK_PLACEHOLDER,
  APP_STORE_ANDROID,
  APP_STORE_IOS,
  APP_STORE_PLACEHOLDER,
  SUGGESTED_CREATIVES_COUNT,
  PRODUCT_URL_PLACEHOLDER,
  PRODUCT_LABEL,
  SERVICE_URL_PLACEHOLDER,
  SERVICE_LABEL,
  BUTTON_SELECT_CONTENT,
} from "../../../../i18n/keysTranslations";
import SelectBusinessAccount from "../../../Meta/SelectBusinessAccount";
import TextField from "../../../Form/TextField";
import {
  DELAY_DEBOUNCE,
  GOOGLE,
  BORDER_RADIUS,
  GOOGLE_IMAGE_FORMATS,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  GOOGLE_APP_STORE,
  APPLE_APP_STORE,
  googleCurrenciesWithDecimals,
  VIDEO_FORMATS,
  ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION,
  ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION,
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
  ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP,
} from "../../../../utils/constants";
import _ from "lodash";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import NumberField from "../../../Form/NumberField";
import DateField from "../../../Form/DateField";
import Grid from "@mui/material/Grid";
import SelectConversionActionsGoogle from "../../../Google/SelectConversionActionsGoogle";
import ContainerVideosYoutube from "../AssetsGroup/ContainerVideosYoutube";
import UploadYoutubeVideoLink from "../AssetsGroup/UploadYoutubeVideoLink";
import { FormProvider } from "react-hook-form";
import { useDispatchApp } from "../../../../lib/redux";
import {
  startGetAudienceProjectionAdsCampaign,
  startGetSuggestedCreativesByBudgetAdsCampaign,
} from "../../../../actions/adsCampaigns";
import { useDebounced } from "../../../../hooks/useDebounce";
import { getExtensionFile, getIsVideoYoutube } from "../../../../utils/string";
import SelectMerchantGoogle from "../Campaign/SelectMerchantGoogle";
import SelectGoogleApp from "../Campaign/SelectGoogleApp";
import { addDays, startOfDay } from "date-fns";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
  GRAY_50,
  GRAY_200,
  GRAY_900,
  GRAY_600,
} from "../../../../utils/colors";
import MediaUrlsAnalysisContainer from "../../../Common/MediaUrlsAnalysisContainer";
import IconButtonAddContent from "../../../Buttons/IconButtonAddContent";
import { formatMediaUrl } from "../../../../services/adsCampaigns";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../../../utils/regex";
import { useHydratePendingGalleryCreatives } from "../../../../hooks/useHydratePendingGalleryCreatives";

const GenerateCampaignFormGoogle = ({
  initialData,
  campaignID,
  generatedPlatforms = {},
  context = {},
  galleryHydrationRef,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert,
  resumeValues = null,
}) => {
  const config = useGenerateCampaignFormGoogle({
    ...context,
    campaignID,
    generatedPlatforms,
    onCallbackSave,
    onExitToPlatformSelection,
    verifyBlockAlert,
    resumeValues,
  });
  const currentAnswer = config.currentAnswer;
  const fieldsConfig = config.fields;
  const { setValue, getValues } = config.utils;

  const steps = config.steps;
  const isLoadingDefaults = config.isLoadingDefaults;

  useEffect(() => {
    if (isLoadingDefaults || _.isEmpty(initialData)) return;

    Object.keys(initialData).forEach((key) => {
      if (key === "destinationURL" && initialData?.[key]) {
        setValue("destinationURL", initialData?.[key]);
      }
      if (
        key === "objective" &&
        initialData[key] &&
        optionsCampaignsTypeGoogleAds.find(
          (option) => option.value === initialData?.[key]
        )
      ) {
        setValue("objective", initialData?.[key]);
      }
    });
    // eslint-disable-next-line
  }, [initialData, isLoadingDefaults]);

  useHydratePendingGalleryCreatives({
    initialData,
    isLoadingDefaults,
    currentAnswer,
    creativeStepName: "imageUrls",
    fieldName: "imageUrls",
    platformKey: GOOGLE,
    galleryHydrationRef,
    setValue,
    getValues,
    acceptedFormats: GOOGLE_IMAGE_FORMATS,
  });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {steps.map((step) => {
          const fieldConfig = fieldsConfig[step];
          if (!fieldConfig.isVisible || currentAnswer !== fieldConfig.name)
            return null;

          return (
            <CommonQuestionContainer
              key={fieldConfig.name}
              formConfig={config}
              fieldConfig={fieldConfig}
              context={context}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const ObjectiveQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  return (
    <Select
      options={optionsCampaignsTypeGoogleAds}
      name={fieldConfig.name}
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
    />
  );
};

const IdentitiesQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue } = fieldConfig.utils;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <SelectBusinessAccount
        variant="filled"
        label={t(AD_ACCOUNT_GOOGLE_PLACEHOLDER)}
        control={control}
        platform={GOOGLE}
        name={`adAccountID`}
        onChangeAction={(value) => {
          setValue(`integrationID`, value?.integrationID);
          setValue(`conversionActions`, []);
        }}
        fullWidth={true}
      />
    </Box>
  );
};

const DestinationURLQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { watch } = fieldConfig.utils;
  const objective = watch("objective");
  const isAppCampaign = objective === ADS_CAMPAIGN_TYPE_APP_OPTION;

  return (
    <TextField
      control={control}
      name={fieldConfig.name}
      fullWidth
      placeholder={t(
        isAppCampaign
          ? DEEP_LINK_PLACEHOLDER
          : CREATE_CAMPAIGN_TRAFFIC_LINK_PLACEHOLDER
      )}
      variant="filled"
    />
  );
};

const DatesQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { watch, setValue } = fieldConfig.utils;
  const startDate = watch("startDate");

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <DateField
          control={control}
          name="startDate"
          label={t(START_DATE_PLACEHOLDER)}
          actionsChange={() => {
            setValue("endDate", null);
          }}
          fullWidth
          minDate={startOfDay(new Date())}
          showStartNowButton={true}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DateField
          control={control}
          name="endDate"
          clearable={true}
          label={t(END_DATE_PLACEHOLDER)}
          defaultCalendarMonth={addDays(startDate || new Date(), 1)}
          fullWidth
          minDate={startDate ? new Date(startDate) : new Date()}
        />
      </Grid>
    </Grid>
  );
};

const BudgetQuestion = ({ fieldConfig, control, currency, context }) => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <TextField
        control={control}
        name={fieldConfig.name}
        placeholder={t(CREATE_ADS_CAMPAIGN_BUDGET_PLACEHOLDER)}
        fullWidth
        variant="filled"
        InputProps={{
          inputComponent: googleCurrenciesWithDecimals.includes(currency)
            ? NumberDecimalField
            : NumberField,
        }}
        helperText={t(VALUE_EXPRESSED_IN, {
          currency,
        })}
      />

      <Projection
        fieldConfig={fieldConfig}
        context={context}
        currency={currency}
      />
    </Box>
  );
};

const MerchantCenterIDQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();

  const { getValues } = fieldConfig.utils;
  const integrationID = getValues("integrationID");
  const adAccountID = getValues("adAccountID");

  return (
    <SelectMerchantGoogle
      label={t(MERCHANT_CENTER_ID_PLACEHOLDER)}
      name={fieldConfig.name}
      control={control}
      variant="filled"
      fullWidth
      integrationID={integrationID}
      adAccountID={adAccountID}
    />
  );
};

const AppIDQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { watch, setValue, formState } = fieldConfig.utils;
  const appStore = watch("appStore");
  const errors = formState?.errors || {};

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
      }}
    >
      <FormControl component="fieldset">
        <FormLabel component="legend">{t(APP_STORE_PLACEHOLDER)}</FormLabel>
        <RadioGroup
          row
          value={appStore}
          onChange={(e) => {
            setValue("appStore", e.target.value);
            setValue("appID", "");
            setValue("conversionActions", []);
          }}
        >
          <FormControlLabel
            value={GOOGLE_APP_STORE}
            control={<Radio />}
            label={t(APP_STORE_ANDROID)}
          />
          <FormControlLabel
            value={APPLE_APP_STORE}
            control={<Radio />}
            label={t(APP_STORE_IOS)}
          />
        </RadioGroup>
      </FormControl>
      {appStore && (
        <SelectGoogleApp
          control={control}
          errors={errors}
          name="appID"
          label={t(APP_PLACEHOLDER)}
          store={appStore}
          fullWidth
          variant="filled"
          placeholder={t(APP_PLACEHOLDER)}
        />
      )}
    </Box>
  );
};

const ConversionActionsQuestion = ({ fieldConfig, control }) => {
  const { watch } = fieldConfig.utils;
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");
  const objective = watch("objective");
  const appStore = watch("appStore");
  const appID = watch("appID");
  const { t } = useTranslationApp();

  return (
    <SelectConversionActionsGoogle
      label={t(CONVERSION_ACTIONS_PLACEHOLDER)}
      name="conversionActions"
      integrationID={integrationID}
      adAccountID={adAccountID}
      objective={objective}
      appStore={appStore}
      appID={appID}
      variant="filled"
      fullWidth
      control={control}
    />
  );
};

const ImageUrlsQuestion = ({ fieldConfig, currency }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [suggestedCreativesCount, setSuggestedCreativesCount] = useState(null);
  const hasFetchedSuggestedCreativesRef = useRef(false);
  const { setValue, getValues, watch } = fieldConfig.utils;
  const imageUrls = watch("imageUrls") || [];
  const videoUrls = watch("videoUrls") || [];
  const objective = watch("objective");
  const context = watch("context");
  const template = watch("template");
  const multipleItems = watch("multipleItems") || [];

  useEffect(() => {
    if (hasFetchedSuggestedCreativesRef.current) return;

    const budget = getValues("budget");
    const typeBudget = getValues("typeBudget");
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");

    const shouldSkip =
      !budget || !typeBudget || !currency || fieldConfig.name !== "imageUrls";

    if (shouldSkip) return;

    hasFetchedSuggestedCreativesRef.current = true;
    setSuggestedCreativesCount(null);

    const fetchSuggestedCreatives = async () => {
      const response = await dispatch(
        startGetSuggestedCreativesByBudgetAdsCampaign({
          platform: GOOGLE,
          budget,
          typeBudget,
          currency,
          startDate,
          endDate,
        })
      );

      const count =
        response.ok && response.data?.suggestedCreativesByBudget
          ? response.data.suggestedCreativesByBudget
          : null;

      setSuggestedCreativesCount(count);
    };

    fetchSuggestedCreatives();
  }, [currency, dispatch, fieldConfig.name, getValues]);

  const mediaUrlsAnalysisRef = useRef(null);

  const onCallbackCampaignMediaImport = ({ entries }) => {
    if (!entries?.length) return;
    const nextImages = [...(getValues("imageUrls") || [])];
    const nextVideosRaw = getValues("videoUrls") || [];
    const nextVideos = nextVideosRaw.filter(Boolean);

    entries.forEach((entry) => {
      if (entry?.isCarousel && entry?.carouselImages?.length > 0) {
        nextImages.push(entry);
        return;
      }
      const url = entry?.url;
      if (!url) return;
      const ext = getExtensionFile(url);
      if (VIDEO_FORMATS.includes(ext)) {
        nextVideos.push(url);
      } else {
        nextImages.push(entry);
      }
    });

    const normalizedVideos = nextVideos.length > 0 ? nextVideos : [""];
    setValue("imageUrls", nextImages);
    setValue("videoUrls", normalizedVideos);
    requestAnimationFrame(() => {
      mediaUrlsAnalysisRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const onGetMediaUrls = ({ videoUrls, imageUrls }) => {
    const mediaUrls = [
      ...imageUrls,
      ...videoUrls.map((videoUrl) => ({ url: videoUrl })),
    ].filter((mediaUrl) => mediaUrl.url);
    return mediaUrls;
  };

  const mediaUrls = onGetMediaUrls({ imageUrls, videoUrls });

  // Use URL as identifier to avoid sync issues with async operations
  const onChangeItem = ({ url, data = {} }) => {
    if (!url) return; // Safety check
    const isVideoYoutube = getIsVideoYoutube(url);
    if (isVideoYoutube) return;

    // Find the item in imageUrls array (not in combined mediaUrls)
    const safeImageUrls = imageUrls || [];
    const index = safeImageUrls.findIndex((item) => item?.url === url);
    if (index === -1) return; // Element no longer exists or is a video, ignore update

    const currentData = safeImageUrls[index];
    if (!currentData) return; // Safety check
    const newData = {
      ...currentData,
      ...data,
    };
    setValue(`imageUrls.[${index}]`, newData);
  };
  const onRemoveItem = (index) => {
    const currentData = mediaUrls[index];
    const isVideoYoutube = getIsVideoYoutube(currentData.url);
    if (isVideoYoutube) return;

    const realIndex = mediaUrls.findIndex(
      (mediaUrl) => mediaUrl.url === currentData.url
    );

    if (realIndex === -1) return;

    setValue(
      "imageUrls",
      imageUrls.filter((_, i) => i !== realIndex)
    );
  };

  const isMultipleTemplate = template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION;
  const hasMultipleItemsContent =
    multipleItems?.length > 0 &&
    multipleItems.some((item) => item?.imageUrls?.length > 0);
  const hasValidVideos = videoUrls?.filter(Boolean)?.length > 0;
  const hasContent = isMultipleTemplate
    ? hasMultipleItemsContent
    : imageUrls?.length > 0 || hasValidVideos;
  const shouldShowSuggestedCreativesHint =
    suggestedCreativesCount !== null && !hasContent && !isMultipleTemplate;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
      }}
    >
      {isMultipleTemplate ? (
        <MultipleItemsQuestion
          fieldConfig={fieldConfig}
          suggestedCreativesCount={suggestedCreativesCount}
        />
      ) : (
        <MediaUrlsAnalysisContainer
          containerRef={mediaUrlsAnalysisRef}
          mediaUrls={mediaUrls}
          onRemoveItem={onRemoveItem}
          onChangeItem={onChangeItem}
          objective={objective}
          campaignContext={context}
          platform={GOOGLE}
          suggestedCreativesCount={hasContent ? suggestedCreativesCount : null}
          onCallbackCampaignMediaImport={onCallbackCampaignMediaImport}
        />
      )}
      {shouldShowSuggestedCreativesHint && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            textAlign: "center",
            color: "text.secondary",
            position: "absolute",
            bottom: -120,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {t(SUGGESTED_CREATIVES_COUNT, {
            count: suggestedCreativesCount,
          })}
        </Typography>
      )}
    </Box>
  );
};

const MultipleItemsQuestion = ({ fieldConfig, suggestedCreativesCount }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const {
    setValue,
    getValues,
    watch,
    campaignID,
    campaignContext,
    language,
    businessOffering,
  } = fieldConfig.utils;
  const lastItemRef = useRef(null);
  const previousLengthRef = useRef();
  const watchedMultipleItems = watch("multipleItems");
  const multipleItems = useMemo(
    () => watchedMultipleItems || [],
    [watchedMultipleItems]
  );
  const integrationID = getValues("integrationID");
  const adAccountID = getValues("adAccountID");

  const isPhysicalProduct =
    businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT;

  const { userID } = dispatch(getPathDatabaseByRole());

  const onRemoveItem = (index) => {
    if (multipleItems.length <= 1) {
      const only = multipleItems[0] || {};
      setValue("template", ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION);
      setValue("destinationURL", only.destinationUrl || "");
      setValue("imageUrls", only.imageUrls || []);
      setValue(
        "videoUrls",
        (only.videoUrls || []).filter(Boolean).length > 0
          ? only.videoUrls.filter(Boolean)
          : [""]
      );
      setValue("multipleItems", [
        { destinationUrl: "", imageUrls: [], videoUrls: [""] },
      ]);
      // destinationURL + videoUrls reappear — keep wizard on creatives step
      const singularVisibleSteps = [
        "identities",
        "objective",
        "merchantCenterID",
        "conversionActions",
        "destinationURL",
        "budget",
        "videoUrls",
        "imageUrls",
      ];
      const imageStepIndex = singularVisibleSteps.indexOf("imageUrls");
      if (imageStepIndex >= 0) {
        setValue("step", imageStepIndex);
      }
      return;
    }
    setValue(
      "multipleItems",
      multipleItems.filter((_, i) => i !== index)
    );
  };

  const updateItem = (index, patch) => {
    const updatedItems = [...multipleItems];
    updatedItems[index] = {
      ...updatedItems[index],
      ...patch,
    };
    setValue("multipleItems", updatedItems);
  };

  const onChangeItemUrl = (index, url) => {
    updateItem(index, { destinationUrl: url });
  };

  const onAddImageUrls = (index, contents) => {
    const contentsFormatted = contents.map((content) =>
      formatMediaUrl(content)
    );
    updateItem(index, {
      imageUrls: [
        ...(multipleItems[index].imageUrls || []),
        ...contentsFormatted,
      ],
    });
  };

  const onRemoveImageUrl = (itemIndex, mediaIndex) => {
    updateItem(itemIndex, {
      imageUrls: multipleItems[itemIndex].imageUrls.filter(
        (_, i) => i !== mediaIndex
      ),
    });
  };

  const onChangeImageUrlItem = (itemIndex, mediaIndex, data) => {
    const updatedImages = [...(multipleItems[itemIndex].imageUrls || [])];
    updatedImages[mediaIndex] = {
      ...updatedImages[mediaIndex],
      ...data,
    };
    updateItem(itemIndex, { imageUrls: updatedImages });
  };

  useEffect(() => {
    if (previousLengthRef.current === undefined) {
      previousLengthRef.current = multipleItems?.length || 0;
      return;
    }
    const currentLength = multipleItems?.length || 0;
    if (currentLength > previousLengthRef.current) {
      requestAnimationFrame(() => {
        lastItemRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    }
    previousLengthRef.current = currentLength;
  }, [multipleItems]);

  return (
    <FormProvider {...fieldConfig.utils}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 2,
          pb: 1,
        }}
      >
        {multipleItems.map((item, itemIndex) => (
          <Box
            key={itemIndex}
            ref={
              itemIndex === multipleItems.length - 1 ? lastItemRef : undefined
            }
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 2,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: BORDER_RADIUS,
              backgroundColor: "fields.background",
              boxShadow: BOX_SHADOW_FIELDS,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {t(isPhysicalProduct ? PRODUCT_LABEL : SERVICE_LABEL, {
                  number: itemIndex + 1,
                })}
              </Typography>
              <IconButton size="small" onClick={() => onRemoveItem(itemIndex)}>
                <Trash2 size={18} />
              </IconButton>
            </Box>

            <TextFieldMUI
              value={item.destinationUrl}
              onChange={(e) => onChangeItemUrl(itemIndex, e.target.value)}
              placeholder={t(
                isPhysicalProduct
                  ? PRODUCT_URL_PLACEHOLDER
                  : SERVICE_URL_PLACEHOLDER
              )}
              fullWidth
              name="destinationUrl"
              variant="filled"
              size="small"
              hiddenLabel
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "fields.background",
                  borderRadius: BORDER_RADIUS,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: BOX_SHADOW_FIELDS,
                },
              }}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <IconButtonAddContent
                mode="button"
                color="secondary"
                size="small"
                disabled={
                  !item.destinationUrl ||
                  !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(
                    encodeURI(item.destinationUrl)
                  )
                }
                pathStorage={`adsCampaigns/${userID}/`}
                pathRTDB={`adsContentsUploaded/${userID}/${campaignID}`}
                modeUpload="sync"
                initialValues={{
                  language,
                  campaignContext,
                  websiteUrl: item.destinationUrl,
                  platform: GOOGLE,
                }}
                onAddContent={(contents) => onAddImageUrls(itemIndex, contents)}
                multiple={true}
                acceptedFormats={GOOGLE_IMAGE_FORMATS}
                textButton={BUTTON_SELECT_CONTENT}
                showGalleryGoogleOption={true}
                showGenerateWithAIOption={true}
                sx={{
                  alignItems: "flex-start",
                }}
                sxButton={{
                  height: 36,
                  mb: 1,
                  fontSize: "0.75rem",
                }}
                propsPostsAdsPlatforms={{
                  [GOOGLE]: {
                    integrationID,
                    adAccountID,
                  },
                }}
              />

              {item.imageUrls?.length > 0 && (
                <MediaUrlsAnalysisContainer
                  itemSize={100}
                  mediaUrls={item.imageUrls}
                  onRemoveItem={(mediaIndex) =>
                    onRemoveImageUrl(itemIndex, mediaIndex)
                  }
                  onChangeItem={({ url, data }) => {
                    const mediaIndex = (
                      multipleItems[itemIndex].imageUrls || []
                    ).findIndex((img) => img?.url === url);
                    if (mediaIndex === -1) return;
                    onChangeImageUrlItem(itemIndex, mediaIndex, data);
                  }}
                  objective={null}
                  campaignContext={campaignContext}
                  platform={GOOGLE}
                  disableAnalysis={true}
                  suggestedCreativesCount={suggestedCreativesCount}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <UploadYoutubeVideoLink
                name={`multipleItems.${itemIndex}.videoUrls`}
                language={language}
                maxVideos={ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP}
              />
              <ContainerVideosYoutube
                name={`multipleItems.${itemIndex}.videoUrls`}
                showUrlInput={false}
                maxVideos={ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </FormProvider>
  );
};

const VideoUrlsQuestion = ({ fieldConfig }) => {
  const { watch } = fieldConfig.utils;
  const language = watch("language");

  return (
    <FormProvider {...fieldConfig.utils}>
      <UploadYoutubeVideoLink
        name="videoUrls"
        language={language}
        maxVideos={ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP}
      />
      <ContainerVideosYoutube
        name="videoUrls"
        showUrlInput={false}
        maxVideos={ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP}
      />
    </FormProvider>
  );
};

const Projection = ({ fieldConfig, context, currency }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState("");
  const abortControllerRef = useRef(null);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const { getValues, watch } = fieldConfig.utils;
  const budget = watch("budget");
  const locationsContext = context?.locations || { locations: [] };
  const locations = locationsContext?.locations?.map(
    (location) => location.name
  );
  const objectContext = {
    locations,
    typeBudget: getValues("typeBudget"),
    budget: getValues("budget"),
    startDate: getValues("startDate"),
    endDate: getValues("endDate"),
    platform: GOOGLE,
    currency,
  };

  const onGetInitialData = useDebounced(async () => {
    setText("");
    if (!objectContext.budget || budget < 1) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    const response = await dispatch(
      startGetAudienceProjectionAdsCampaign({
        ...objectContext,
        signal: controller.signal,
      })
    );
    if (response?.aborted) return;
    if (response.ok) {
      setText(response.data.text);
    } else {
      setText(t(ERROR_OCCURRED));
    }
    setIsLoading(false);
  }, [DELAY_DEBOUNCE * 1.5]);

  useEffect(() => {
    onGetInitialData();
    return () => abortControllerRef.current?.abort();
    // eslint-disable-next-line
  }, [budget]);

  if (!text && !isLoading) return null;

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      {isLoading ? (
        <>
          <Skeleton variant="rectangular" width="100%" height={60} />
        </>
      ) : (
        <Typography variant="body2">{text}</Typography>
      )}
    </Box>
  );
};

const ImageUrlsSubtitle = ({ fieldConfig, t }) => {
  const subtitleText = fieldConfig.subtitle
    ? t(fieldConfig.subtitle, fieldConfig.subtitleExtrapolation)
    : "";

  const requirements = Array.isArray(fieldConfig.recommendedSizes)
    ? fieldConfig.recommendedSizes.map((key) => t(key))
    : [];

  return (
    <Box
      sx={{
        backgroundColor: GRAY_50,
        border: `1px solid ${GRAY_200}`,
        borderRadius: BORDER_RADIUS,
        width: "100%",
        p: 2,
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.8 }}>
        {subtitleText && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: GRAY_900,
              fontSize: "16px",
              "& b": {
                fontWeight: 600,
                color: GRAY_900,
              },
            }}
            dangerouslySetInnerHTML={{
              __html: subtitleText,
            }}
          />
        )}
        {requirements.length > 0 && (
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {requirements.map((requirementText, index) => (
              <Typography
                key={index}
                variant="caption"
                component="li"
                sx={{
                  fontSize: "14px",
                  color: GRAY_600,
                  lineHeight: 1.5,
                  listStyle: "none",
                  position: "relative",
                  pl: 1.5,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "0.5rem",
                    height: 6,
                    width: 6,
                    borderRadius: "50%",
                    backgroundColor: GRAY_900,
                  },
                  "& b": {
                    fontWeight: 600,
                    color: GRAY_900,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: requirementText,
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const COMPONENTS = {
  objective: <ObjectiveQuestion />,
  merchantCenterID: <MerchantCenterIDQuestion />,
  appID: <AppIDQuestion />,
  conversionActions: <ConversionActionsQuestion />,
  identities: <IdentitiesQuestion />,
  destinationURL: <DestinationURLQuestion />,
  dates: <DatesQuestion />,
  budget: <BudgetQuestion />,
  imageUrls: <ImageUrlsQuestion />,
  videoUrls: <VideoUrlsQuestion />,
};

const CommonQuestionContainer = ({ formConfig, fieldConfig, context }) => {
  const { t } = useTranslationApp();

  const progress = formConfig.progress;
  const { control, setValue } = fieldConfig.utils;
  const error = formConfig.error;

  const COMPONENT = COMPONENTS[fieldConfig.name];

  if (!COMPONENT) return null;

  return (
    <>
      <LinearProgress
        sx={{
          width: "100%",
          mb: 3,
          maxWidth: 600,
          backgroundColor: "rgba(0,0,0,0.2)",
          "& .MuiLinearProgress-bar": { backgroundColor: "black" },
        }}
        variant="determinate"
        value={progress}
      />
      <AnswerContainer sx={{ display: "flex", height: "100%" }}>
        <AnswerContainerTitleAndField>
          {fieldConfig.title && (
            <AnswerTitle
              sx={{ mb: 1 }}
              showButton={fieldConfig.showTitleButton}
              buttonText={fieldConfig.buttonTitleText}
              onClickButton={fieldConfig.onClickButtonTitle}
              sxButton={fieldConfig.sxButtonTitle}
              icon={fieldConfig.icon}
            >
              {`${t(fieldConfig.title, fieldConfig.titleExtrapolation)}`}
            </AnswerTitle>
          )}
          {fieldConfig.name === "imageUrls" &&
          (fieldConfig.subtitle || fieldConfig.recommendedSizes) ? (
            <ImageUrlsSubtitle fieldConfig={fieldConfig} t={t} />
          ) : (
            <>
              {fieldConfig.subtitle && (
                <AnswerSubTitle>
                  {t(fieldConfig.subtitle, fieldConfig.subtitleExtrapolation)}
                </AnswerSubTitle>
              )}
              {fieldConfig.recommendedSizes && (
                <AnswerSubTitle>
                  {t(fieldConfig.recommendedSizes)}
                </AnswerSubTitle>
              )}
            </>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 1,
                backgroundColor: "fields.background",
                color: "background.paperContrastText",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: BORDER_RADIUS,
                boxShadow: BOX_SHADOW_FIELDS,
              }}
            >
              {error}
            </Alert>
          )}
          {React.cloneElement(COMPONENT, {
            fieldConfig,
            control,
            setValue,
            error,
            currency: formConfig.currency,
            context,
          })}
        </AnswerContainerTitleAndField>
        {fieldConfig.showActionsButtons && (
          <>
            <ActionsButtons
              onPreviousStep={fieldConfig.onPreviousStep}
              showPreviousButton={fieldConfig.showPreviousButton}
              showSkipButton={fieldConfig.showSkipButton}
              onSkipStep={fieldConfig.onSkipStep}
              onNextStep={fieldConfig.onNextStep}
              showNextButton={fieldConfig.showNextButton}
              nextButtonText={fieldConfig.nextButtonText}
              saveButtonText={fieldConfig.saveButtonText}
              showSaveButton={fieldConfig.isFinishQuestion}
              disabledSaveButton={fieldConfig.disabledFinishButton}
              onFinish={fieldConfig.onFinish}
              showLoadingComponent={fieldConfig.showLoadingComponent}
              customComponentPreviousNext={
                fieldConfig.customComponentPreviousNext
              }
              loading={formConfig.isCreating}
            />
          </>
        )}
      </AnswerContainer>
    </>
  );
};

export default GenerateCampaignFormGoogle;
